import React, { useEffect, useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { getListingPreview } from '@/lib/marketplace';
import { iconDataUri } from '@/lib/sitePages';

// ─── A live thumbnail of a listed site ────────────────────────────────────────
// Thumbnails need a headless browser, which Hostinger does not have, so a card
// shows the site itself: its home page in a scaled, click-through frame. The
// site is fetched only once the card scrolls into view, so a page of twelve
// listings does not download twelve sites up front.
//
// sandbox="allow-scripts" without allow-same-origin: the site's own script
// runs (generated sites reveal sections from it), in an opaque origin that
// cannot reach this page's storage — where the login token lives.

function thumbDoc({ html, css, js }) {
  const safeJs = String(js || '').replace(/<\/script/gi, '<\\/script');
  return `<!DOCTYPE html><html><head><meta charset="utf-8">`
    + '<meta name="viewport" content="width=1280">'
    + '<script src="https://cdn.tailwindcss.com"></script>'
    + `<style>${css || ''}</style>`
    + '<style>html,body{overflow:hidden!important;pointer-events:none!important}</style>'
    + `</head><body>${html || ''}<script>try{${safeJs}}catch(e){}</script></body></html>`;
}

export default function ListingThumb({ listingId, title, iconSvg, className = 'h-44' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [doc, setDoc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !listingId) return undefined;
    let cancelled = false;
    getListingPreview(listingId)
      .then((site) => { if (!cancelled) setDoc(thumbDoc(site)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [visible, listingId]);

  const icon = iconDataUri(iconSvg);

  return (
    <div ref={ref} className={`relative overflow-hidden bg-zinc-800 ${className}`}>
      {doc && !failed ? (
        <iframe
          title={`${title || 'Site'} preview`}
          srcDoc={doc}
          sandbox="allow-scripts"
          loading="lazy"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute top-0 left-0 border-none origin-top-left pointer-events-none bg-snow"
          style={{ width: '400%', height: '400%', transform: 'scale(0.25)' }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          {icon
            ? <img src={icon} alt="" className="w-12 h-12 rounded-xl opacity-80" />
            : <Globe className={`w-10 h-10 text-zinc-700 ${!failed ? 'animate-pulse' : ''}`} />}
        </div>
      )}
    </div>
  );
}
